import { useConfirmationModal } from '@common/confirmation.hooks';
import { useProjectId } from '@config/config.hooks';
import {
  Accordion,
  Column,
  ComponentType,
  createTableCell,
  createTableColumn,
  Icon,
  IOption,
  LoadingButton,
  PaginatedTable,
  toastMessage,
  ToastMessage,
  useForm,
  useModal,
} from '@gcl/react-core';
import { IProjectTeamOption } from '@projectTeam/projectTeam.models';
import { IProjectRoleOption } from '@role/projectRole/projectRole.models';
import { useQueryClient } from '@tanstack/react-query';
import { ETransmittalStatus, ETransmittalType } from '@transmittal/transmittal.constants';
import {
  ICreateProjectTransmittalDistributionRoleRequest,
  ICreateProjectTransmittalDistributionTeamRequest,
  ICreateProjectTransmittalDistributionUserRequest,
  IDeleteProjectTransmittalDistributionRequest,
  IProjectTransmittalDistribution,
} from '@transmittal/transmittal.models';
import {
  useCreateTransmittalDistributionRole,
  useCreateTransmittalDistributionTeam,
  useCreateTransmittalDistributionUser,
  useDeleteTransmittalDistributionRole,
  useDeleteTransmittalDistributionTeam,
  useDeleteTransmittalDistributionUser,
} from '@transmittal/transmittal.queries';
import { IProjectUserProfileOption } from '@userProfile/projectUserProfile.models';
import * as React from 'react';
import { useState, useMemo } from 'react';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { TransmittalDetailDistributionForm } from './TransmittalDetailDistributionForm';
import { IDistribution } from './transmittalDetailPage.models';
import { transmittalDistributionFormKeys } from './transmittalDetailPage.constants';

interface ITransmittalDetailDistributionProps {
  isReadOnly: boolean;
  masterScheduleId?: number;
  transmittalId?: number;
  transmittalType?: ETransmittalType;
  transmittalStatus?: ETransmittalStatus;
  distributionData?: IProjectTransmittalDistribution;
  isAnyActionLoading?: boolean;
}
export const TransmittalDetailDistribution: React.FunctionComponent<ITransmittalDetailDistributionProps> = ({
  isReadOnly,
  masterScheduleId,
  transmittalId,
  transmittalType,
  transmittalStatus,
  distributionData,
  isAnyActionLoading,
}) => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const [distributionItemToDelete, setDistributionItemToDelete] = useState<IDistribution>();

  const isDeleteDistributionEnable = useMemo(() => {
    const totalNumOfDistribution =
      (distributionData?.distributionRole ?? []).length +
      (distributionData?.distributionTeam ?? []).length +
      (distributionData?.distributionUser ?? []).length;
    return totalNumOfDistribution > 1;
  }, [JSON.stringify(distributionData)]);

  const columns = React.useMemo(() => {
    const defaultCols = [
      {
        Header: 'Distribution',
        accessor: 'distribution',
        disableResizing: false,
        disableSortBy: false,
      },
      {
        Header: 'Attention',
        accessor: 'attention',
        disableResizing: false,
        disableSortBy: false,
      },
    ];
    if (isReadOnly) return defaultCols;
    return [
      createTableColumn({
        Header: '',
        accessor: 'id',
        width: 30,
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IDistribution, index: number, cellExtras: { [key: string]: any }) => {
            return (
              <Icon
                iconName="Trash"
                className="gcl-clickable gcl-mr-sm gcl-ic-btn-lg gcl-fc-danger-400"
                onClick={() => onClickItemDelete(item, cellExtras.isDeleteDistributionEnable)}
                disabled={isAnyActionLoading}
              />
            );
          },
        ),
      } as Column),
      ...defaultCols,
    ];
  }, [isAnyActionLoading, transmittalStatus]);

  const distributionList: IDistribution[] = useMemo(() => {
    const roles: IDistribution[] =
      distributionData?.distributionRole?.map((item) => {
        return {
          distribution: `${item.projectCompany.name} - ${item.projectRole?.role?.name}`,
          attention: item.projectUserProfiles?.map((p) => p.userProfile?.displayName).join(', '),
          roleData: item,
        };
      }) ?? [];
    const teams: IDistribution[] =
      distributionData?.distributionTeam?.map((item) => {
        return {
          distribution: `${item.projectCompany.name} - ${item.projectTeam?.teamName}`,
          attention: item.projectUserProfiles?.map((p) => p.userProfile?.displayName).join(', '),
          teamData: item,
        };
      }) ?? [];
    const users: IDistribution[] =
      distributionData?.distributionUser?.map((item) => {
        return {
          distribution: 'Individual',
          attention: item.projectUserProfile.userProfile?.displayName,
          individualData: item,
        };
      }) ?? [];
    return [...roles, ...teams, ...users];
  }, [JSON.stringify(distributionData)]);

  const useDistributionFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });
  const { openPortal: openDistributionFormPortal, renderModal, closePortal: closeDistributionFormPortal } = useModal();

  const { renderModal: renderDeleteConfirmation, openPortal: openDeleteConfirmation } = useConfirmationModal({
    content: `Confirm to Delete ${distributionItemToDelete?.distribution ?? distributionItemToDelete?.attention}?`,
    onConfirm: () => {
      onDeleteItem();
    },
  });

  const onSuccessCrudDistribution = () => {
    queryClient.invalidateQueries([projectId, transmittalId, FETCH_QUERIES_NAME.TRANSMITTAL_DISTRIBUTION]);
  };

  const {
    mutate: mutateCreateDistributionTeam,
    error: errorCreateDistributionTeam,
    isSuccess: isSuccessCreateDistributionTeam,
    isError: isErrorCreateDistributionTeam,
    isLoading: isLoadingCreateDistributionTeam,
    reset: resetCreateDistributionTeam,
  } = useCreateTransmittalDistributionTeam({
    onSuccess: onSuccessCrudDistribution,
  });
  const {
    mutate: mutateCreateDistributionRole,
    error: errorCreateDistributionRole,
    isSuccess: isSuccessCreateDistributionRole,
    isError: isErrorCreateDistributionRole,
    isLoading: isLoadingCreateDistributionRole,
    reset: resetCreateDistributionRole,
  } = useCreateTransmittalDistributionRole({
    onSuccess: onSuccessCrudDistribution,
  });
  const {
    mutate: mutateCreateDistributionUser,
    error: errorCreateDistributionUser,
    isSuccess: isSuccessCreateDistributionUser,
    isError: isErrorCreateDistributionUser,
    isLoading: isLoadingCreateDistributionUser,
    reset: resetCreateDistributionUser,
  } = useCreateTransmittalDistributionUser({
    onSuccess: onSuccessCrudDistribution,
  });
  const {
    mutate: mutateDeleteDistributionTeam,
    error: errorDeleteDistributionTeam,
    isSuccess: isSuccessDeleteDistributionTeam,
    isError: isErrorDeleteDistributionTeam,
    isLoading: isLoadingDeleteDistributionTeam,
    reset: resetDeleteDistributionTeam,
  } = useDeleteTransmittalDistributionTeam({
    onSuccess: onSuccessCrudDistribution,
  });
  const {
    mutate: mutateDeleteDistributionRole,
    error: errorDeleteDistributionRole,
    isSuccess: isSuccessDeleteDistributionRole,
    isError: isErrorDeleteDistributionRole,
    isLoading: isLoadingDeleteDistributionRole,
    reset: resetDeleteDistributionRole,
  } = useDeleteTransmittalDistributionRole({
    onSuccess: onSuccessCrudDistribution,
  });
  const {
    mutate: mutateDeleteDistributionUser,
    error: errorDeleteDistributionUser,
    isSuccess: isSuccessDeleteDistributionUser,
    isError: isErrorDeleteDistributionUser,
    isLoading: isLoadingDeleteDistributionUser,
    reset: resetDeleteDistributionUser,
  } = useDeleteTransmittalDistributionUser({
    onSuccess: onSuccessCrudDistribution,
  });

  const handleDistributionFormSubmit = (data: any) => {
    const individualUsers = (data[transmittalDistributionFormKeys.INDIVIDUAL_USER] ??
      []) as IProjectUserProfileOption[];
    const roles = (data[transmittalDistributionFormKeys.USER_ROLE] ?? []) as IOption<IProjectRoleOption, number>[];
    const teams = (data[transmittalDistributionFormKeys.TEAM] ?? []) as IProjectTeamOption[];
    if (teams.length > 0) {
      // add distribution team
      const teamRequest: ICreateProjectTransmittalDistributionTeamRequest[] = teams.map(({ id }) => {
        return {
          projectId,
          masterScheduleId: masterScheduleId ?? 0,
          transmittalId: transmittalId ?? 0,
          transmittalType,
          projectCompanyId: data[transmittalDistributionFormKeys.GROUP]?.data?.id,
          projectTeamId: id ?? 0,
        };
      });
      mutateCreateDistributionTeam(teamRequest);
      closeDistributionFormPortal();
      return;
    }

    if (roles.length > 0) {
      // add distribution role
      const roleRequest: ICreateProjectTransmittalDistributionRoleRequest[] = roles.map(({ key }) => {
        return {
          projectId,
          masterScheduleId: masterScheduleId ?? 0,
          transmittalId: transmittalId ?? 0,
          transmittalType,
          projectCompanyId: data[transmittalDistributionFormKeys.GROUP]?.data?.id,
          projectRoleId: key,
        };
      });
      mutateCreateDistributionRole(roleRequest);
      closeDistributionFormPortal();
      return;
    }

    // add distribution user
    const userRequest: ICreateProjectTransmittalDistributionUserRequest[] = individualUsers.map(({ id }) => {
      return {
        projectId,
        masterScheduleId: masterScheduleId ?? 0,
        transmittalId: transmittalId ?? 0,
        projectUserProfileId: id,
      };
    });
    mutateCreateDistributionUser(userRequest);
    closeDistributionFormPortal();
  };

  const onClickItemDelete = (item: IDistribution, isDeleteDistributionEnableLocal: boolean) => {
    if (!isDeleteDistributionEnableLocal) {
      toastMessage(
        <ToastMessage
          message="At least 1 distribution must be selected"
          toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
        />,
      );
      return;
    }
    setDistributionItemToDelete(item);
    openDeleteConfirmation();
  };
  const onDeleteItem = () => {
    if (!distributionItemToDelete) return;
    const isDeleteTeam = distributionItemToDelete.teamData !== undefined;
    const isDeleteRole = distributionItemToDelete.roleData !== undefined;

    if (isDeleteTeam) {
      const deleteRequest: IDeleteProjectTransmittalDistributionRequest = {
        projectId,
        transmittalId,
        id: distributionItemToDelete.teamData?.id ?? 0,
      };
      mutateDeleteDistributionTeam(deleteRequest);
      return;
    }
    if (isDeleteRole) {
      const deleteRequest: IDeleteProjectTransmittalDistributionRequest = {
        projectId,
        transmittalId,
        id: distributionItemToDelete.roleData?.id ?? 0,
      };
      mutateDeleteDistributionRole(deleteRequest);
      return;
    }
    const deleteRequest: IDeleteProjectTransmittalDistributionRequest = {
      projectId,
      transmittalId,
      id: distributionItemToDelete.individualData?.id ?? 0,
    };
    mutateDeleteDistributionUser(deleteRequest);
  };

  const onRenderListAccordionBody = () => {
    return (
      <PaginatedTable
        allowSelectRow={false}
        columns={columns}
        items={distributionList}
        disablePagination
        cellExtras={{ isDeleteDistributionEnable }}
      />
    );
  };
  return (
    <div>
      <ApiSuccessToast
        successes={[
          {
            isSuccess: isSuccessCreateDistributionTeam,
            message: 'Create distribution team success',
            reset: resetCreateDistributionTeam,
          },
          {
            isSuccess: isSuccessCreateDistributionRole,
            message: 'Create distribution role success',
            reset: resetCreateDistributionRole,
          },
          {
            isSuccess: isSuccessCreateDistributionUser,
            message: 'Create distribution user success',
            reset: resetCreateDistributionUser,
          },
          {
            isSuccess: isSuccessDeleteDistributionTeam,
            message: 'Delete distribution team success',
            reset: resetDeleteDistributionTeam,
          },
          {
            isSuccess: isSuccessDeleteDistributionRole,
            message: 'Delete distribution role success',
            reset: resetDeleteDistributionRole,
          },
          {
            isSuccess: isSuccessDeleteDistributionUser,
            message: 'Delete distribution user success',
            reset: resetDeleteDistributionUser,
          },
        ]}
      />
      <ApiErrorDisplay
        setFormError={useDistributionFormMethods.setError}
        errors={[
          {
            error: errorCreateDistributionTeam,
            genericError: 'Create distribution team fail',
            reset: resetCreateDistributionTeam,
          },
          {
            error: errorCreateDistributionRole,
            genericError: `Create distribution role fail`,
            reset: resetCreateDistributionRole,
          },
          {
            error: errorCreateDistributionUser,
            genericError: `Create distribution user fail`,
            reset: resetCreateDistributionUser,
          },
          {
            error: errorDeleteDistributionTeam,
            genericError: `Delete distribution team fail`,
            reset: resetDeleteDistributionTeam,
          },
          {
            error: errorDeleteDistributionRole,
            genericError: `Delete distribution role fail`,
            reset: resetDeleteDistributionRole,
          },
          {
            error: errorDeleteDistributionUser,
            genericError: 'Delete distribution user fail',
            reset: resetDeleteDistributionUser,
          },
        ]}
      />
      <Accordion
        allowClickHeaderToExpand
        content={{
          id: 'Distribution',
          onRenderHeaderButtons: () => {
            if (isReadOnly) {
              return [];
            }
            return [
              <Icon
                iconName="PlusSquare"
                key="transmittalDistributionAdd"
                className="gcl-clickable gcl-ic-btn-lg gcl-fc-primary-400"
                onClick={(e) => {
                  useDistributionFormMethods.reset();
                  openDistributionFormPortal(e);
                  e.stopPropagation();
                }}
                data-testid="add-distribution"
              />,
            ];
          },
          onRenderBody: onRenderListAccordionBody,
          onRenderHeader: () => {
            return <span>Distribution</span>;
          },
        }}
      />
      {renderDeleteConfirmation(
        isLoadingDeleteDistributionTeam || isLoadingDeleteDistributionRole || isLoadingDeleteDistributionUser,
        isErrorDeleteDistributionTeam || isErrorDeleteDistributionRole || isErrorDeleteDistributionUser,
        isSuccessDeleteDistributionTeam || isSuccessDeleteDistributionRole || isSuccessDeleteDistributionUser,
      )}
      {renderModal({
        large: true,
        title: 'Add new distribution',
        onRenderBody: () => {
          return (
            <TransmittalDetailDistributionForm
              masterScheduleId={masterScheduleId ?? -1}
              useFormMethods={useDistributionFormMethods}
            />
          );
        },
        onRenderFooter: (onHide?: () => void) => {
          return (
            <div>
              <LoadingButton
                type={ComponentType.Primary}
                onClick={() => {
                  useDistributionFormMethods.handleSubmit(handleDistributionFormSubmit)();
                }}
                isLoading={
                  isLoadingCreateDistributionTeam || isLoadingCreateDistributionUser || isLoadingCreateDistributionRole
                }
                isError={
                  isErrorCreateDistributionTeam || isErrorCreateDistributionUser || isErrorCreateDistributionRole
                }
                isSuccess={
                  isSuccessCreateDistributionTeam || isSuccessCreateDistributionUser || isSuccessCreateDistributionRole
                }
                timeToResetSuccessState={3000}
                timeToResetErrorState={5000}
              >
                Add
              </LoadingButton>
            </div>
          );
        },
      })}
    </div>
  );
};
